export const PAGE_WINDOW = 5 // minimum selectable pages shown around the current page

export type PageItem = number | 'dots'

/**
 * Produces a list of page items to render in a pagination control,
 * with an ellipsis placeholder ('dots') inserted wherever the range skips.
 * Indices are zero-based.
 *
 * Guarantees at least PAGE_WINDOW consecutive selectable page numbers
 * whenever `total` exceeds the window size, extending the window toward
 * the opposite edge when the current page is near a boundary.
 */
export const getPageRange = (current: number, total: number): PageItem[] => {
  if (total <= 0) return []
  if (total <= PAGE_WINDOW + 2) return Array.from({ length: total }, (_, i) => i)

  const half = Math.floor((PAGE_WINDOW - 1) / 2)
  let start = Math.max(1, current - half)
  let end = Math.min(total - 2, current + half)
  if (start === 1) end = Math.min(total - 2, start + PAGE_WINDOW - 1)
  if (end === total - 2) start = Math.max(1, end - PAGE_WINDOW + 1)

  const pages = new Set<number>([0, total - 1])
  for (let i = start; i <= end; i++) pages.add(i)
  const sorted = Array.from(pages).sort((a, b) => a - b)
  const result: PageItem[] = []
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push('dots')
    result.push(p)
  })
  return result
}
